/* External dependencies */
import { AnyAction } from 'redux';

/* Local dependencues */
import { LoginActionTypes } from './actions';
import { CognitoError } from './types';

export interface AuthState {
  user: { email: string } | null;

  isAuthenticated: boolean;
  loading: boolean;
  isForgotPasswordSuccess: boolean;
  error: string | null;

  email: string;
  isSignInLoading: boolean;
  signInError: string | null;

  signUpEmail: string;
  signUpPassword: string;
  isSignUpLoading: boolean;
  signUpError: string | null;

  isConfirmSignUpLoading: boolean;
  confirmSignUpError: string | null;

  isForgotPasswordLoading: boolean;
  forgotPasswordError: CognitoError | null;
  forgotPasswordEmail: string;

  isConfirmPasswordLoading: boolean;
  confirmPasswordError: string | null;

  isSignOutLoading: boolean;
  signOutError: string | null;
}

const initialState: AuthState = {
  user: null,

  isAuthenticated: false,
  loading: false,
  isForgotPasswordSuccess: false,
  error: null,

  email: '',
  isSignInLoading: false,
  signInError: null,

  signUpEmail: '',
  signUpPassword: '',
  isSignUpLoading: false,
  signUpError: null,

  isConfirmSignUpLoading: false,
  confirmSignUpError: null,

  isForgotPasswordLoading: false,
  forgotPasswordError: null,
  forgotPasswordEmail: '',

  isConfirmPasswordLoading: false,
  confirmPasswordError: null,

  isSignOutLoading: false,
  signOutError: null,
};

export const authReducer = (state = initialState, action: AnyAction): AuthState => {
  switch (action.type) {
    case LoginActionTypes.SIGNIN:
      return {
        ...state,
        email: action.username,
        isSignInLoading: true,
        signInError: null,
      };

    case LoginActionTypes.SIGNIN_SUCCESS:
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('user', JSON.stringify(action.payload));

      return {
        ...state,
        isAuthenticated: true,
        user: action.payload,
      };

    case LoginActionTypes.SIGNIN_FAILED:
      return {
        ...state,
        isSignInLoading: false,
        signInError: action.error,
      };

    case LoginActionTypes.SIGNUP:
      return {
        ...state,
        signUpEmail: action.username,
        signUpPassword: action.password,
        isSignUpLoading: true,
        signUpError: null,
      };

    case LoginActionTypes.SIGNUP_SUCCESS:
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('user', JSON.stringify(action.payload));

      return {
        ...state,
        isSignUpLoading: false,
        signUpError: null,
      };

    case LoginActionTypes.SIGNUP_FAILED:
      return {
        ...state,
        isSignUpLoading: false,
        signUpError: action.error,
      };

    case LoginActionTypes.RESTORE_SESSION:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };

    case LoginActionTypes.CONFIRM_SIGNUP:
      return {
        ...state,
        isConfirmSignUpLoading: true,
        confirmSignUpError: null,
      };

    case LoginActionTypes.CONFIRM_SIGNUP_SUCCESS:
      return {
        ...state,
        isConfirmSignUpLoading: false,
        confirmSignUpError: null,
      };

    case LoginActionTypes.CONFIRM_SIGNUP_FAILED:
      return {
        ...state,
        isConfirmSignUpLoading: false,
        confirmSignUpError: action.error,
      };

    case LoginActionTypes.FORGOT_PASSWORD:
      return {
        ...state,
        isForgotPasswordLoading: true,
        isForgotPasswordSuccess: false,
        forgotPasswordError: null,
      };

    case LoginActionTypes.FORGOT_PASSWORD_SUCCEEDED:
      return {
        ...state,
        forgotPasswordEmail: action.payload,
        isForgotPasswordSuccess: true,
        forgotPasswordError: null,
      };

    case LoginActionTypes.FORGOT_PASSWORD_FAILED:
      return {
        ...state,
        isForgotPasswordLoading: false,
        isForgotPasswordSuccess: false,
        forgotPasswordError: action.error,
      };

    case LoginActionTypes.CONFIRM_PASSWORD:
      return {
        ...state,
        isConfirmPasswordLoading: true,
        confirmPasswordError: null,
      };

    case LoginActionTypes.CONFIRM_PASSWORD_SUCCESS:
      return {
        ...state,
        isConfirmPasswordLoading: false,
        confirmPasswordError: null,
      };

    case LoginActionTypes.CONFIRM_PASSWORD_FAILED:
      return {
        ...state,
        isConfirmPasswordLoading: false,
        confirmPasswordError: action.error,
      };

    case LoginActionTypes.SET_SIGNUP_EMAIL:
      return {
        ...state,
        signUpEmail: action.payload,
      };

    case LoginActionTypes.RESET_SIGNIN_ERROR:
      return {
        ...state,
        signInError: null,
      };

    case LoginActionTypes.SIGNOUT:
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('user');
      return {
        ...state,
        isSignOutLoading: true,
        signOutError: null,
      };

    case LoginActionTypes.SIGNOUT_SUCCESS:
      return initialState;

    case LoginActionTypes.SIGNOUT_FAILED:
      return {
        ...state,
        isSignOutLoading: false,
        signOutError: action.error,
      };

    default:
      return state;
  }
};
